import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/** Marks the cart events this component raises itself, so it never reacts to its own. */
const SOURCE = 'candy-bar-component';

/**
 * @typedef {object} CandyBarRefs
 * @property {HTMLElement} [root] - Wrapper carrying the main product's id and handle.
 * @property {HTMLTemplateElement} [moneyFormat] - Holds `shop.money_format`.
 * @property {HTMLInputElement[]} [toggles] - One checkbox per accessory.
 * @property {HTMLElement} [total] - Total row, carries the main variant price.
 * @property {HTMLElement} [totalValue] - Where the total is written.
 */

/**
 * @typedef {object} CartLine
 * @property {number} id - Variant id.
 * @property {number} quantity
 * @property {Record<string, string>} properties
 */

/**
 * Accessory picker ("Candy Bar") shown below the variant picker.
 *
 * Horizon's own buy button stays untouched: the component waits for the main product to land in
 * the cart and then adds the checked accessories in a single follow-up request, each as its own
 * cart line.
 *
 * @extends Component<CandyBarRefs>
 */
class CandyBar extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#renderTotal();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);

    super.disconnectedCallback();
  }

  /** Called declaratively by `on:change` on the checkboxes and the variant selects. */
  handleChange() {
    this.#syncRowPrices();
    this.#renderTotal();
  }

  /** @returns {Element | null} */
  get #section() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * Adds the checked accessories once the main product itself was added successfully.
   * @param {Event} event
   */
  #onCartLinesUpdate = (event) => {
    const { promise } = /** @type {{ promise?: Promise<{ detail?: Record<string, unknown> }> }} */ (
      /** @type {unknown} */ (event)
    );

    if (!promise) return;

    promise
      .then(({ detail }) => {
        const { root } = this.refs;
        if (!detail || !root) return;

        // Our own follow-up add carries SOURCE and must not trigger another round.
        if (detail.source !== 'product-form-component' || detail.didError) return;
        if (String(detail.productId ?? '') !== root.dataset.productId) return;

        return this.#addSelectedItems();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[candy-bar] cart event rejected:', error);
      });
  };

  /**
   * Picks the refreshed main price out of the re-rendered section markup.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const { promise } = /** @type {{ promise?: Promise<{ detail?: { html?: Document } }> }} */ (
      /** @type {unknown} */ (event)
    );

    if (!promise) return;

    promise
      .then(({ detail }) => {
        const { total } = this.refs;
        const price = detail?.html?.querySelector('candy-bar-component .candy-bar__total');

        if (!total || !(price instanceof HTMLElement)) return;

        total.dataset.mainPrice = price.dataset.mainPrice ?? total.dataset.mainPrice;
        this.#renderTotal();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[candy-bar] product event rejected:', error);
      });
  };

  /**
   * The accessories the buyer has checked, ready for `/cart/add.js`.
   * @returns {CartLine[]}
   */
  #selectedItems() {
    const { root } = this.refs;
    const handle = root?.dataset.productHandle ?? '';

    /** @type {CartLine[]} */
    const items = [];

    for (const row of this.#rows()) {
      const { toggle, variantId } = row;
      if (!toggle.checked || toggle.disabled || !variantId) continue;

      items.push({
        id: Number(variantId),
        quantity: 1,
        // The leading underscore keeps the property out of the buyer-facing cart line.
        properties: { _zu: handle },
      });
    }

    return items;
  }

  /**
   * One entry per accessory row, with the currently selected variant resolved.
   * @returns {{ element: HTMLElement, toggle: HTMLInputElement, variantId: string, price: number }[]}
   */
  #rows() {
    const toggles = this.refs.toggles ?? [];

    /** @type {{ element: HTMLElement, toggle: HTMLInputElement, variantId: string, price: number }[]} */
    const rows = [];

    for (const toggle of toggles) {
      const element = toggle.closest('.candy-bar__item');
      if (!(element instanceof HTMLElement)) continue;

      const select = element.querySelector('.candy-bar__variants');
      const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : undefined;

      rows.push({
        element,
        toggle,
        variantId: option?.value ?? element.dataset.variantId ?? '',
        price: Number(option?.dataset.price ?? element.dataset.price ?? 0),
      });
    }

    return rows;
  }

  /** Keeps each row's price label in step with its variant select. */
  #syncRowPrices() {
    for (const { element, price } of this.#rows()) {
      const label = element.querySelector('.candy-bar__price');
      if (label instanceof HTMLElement) label.textContent = this.#money(price);
    }
  }

  #renderTotal() {
    const { total, totalValue } = this.refs;
    if (!total || !totalValue) return;

    const main = Number(total.dataset.mainPrice ?? 0);
    const extras = this.#rows()
      .filter(({ toggle }) => toggle.checked && !toggle.disabled)
      .reduce((sum, { price }) => sum + price, 0);

    totalValue.textContent = this.#money(main + extras);
  }

  /**
   * @param {number} value - Amount in minor units.
   * @returns {string}
   */
  #money(value) {
    const format = this.refs.moneyFormat?.content.textContent?.trim() || '{{amount}}';

    return formatMoney(value, format, Shopify.currency.active);
  }

  async #addSelectedItems() {
    const items = this.#selectedItems();
    if (!items.length) return;

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(/** @returns {id is string} */ (id) => typeof id === 'string');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      const result = await response.json();

      if (result.status) throw new Error(result.message || 'Add to cart failed');

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: SOURCE,
          sections: result.sections,
          itemCount: cart.item_count,
          didError: false,
        },
      });

      this.#clearSelection();
    } catch (error) {
      deferred.reject(error);
      console.error('[candy-bar] adding accessories failed:', error);
    }
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }

  #clearSelection() {
    for (const toggle of this.refs.toggles ?? []) toggle.checked = false;

    this.#renderTotal();
  }
}

if (!customElements.get('candy-bar-component')) {
  customElements.define('candy-bar-component', CandyBar);
}
